import { AfterViewInit, Component, OnInit } from "@angular/core";
import { DialogService } from "src/app/services/dialog.service";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin, { DateClickArg } from "@fullcalendar/interaction";
import {
  CalendarOptions,
  EventClickArg,
  EventMountArg,
} from "@fullcalendar/core";
import { EventDialogComponent } from "../event-dialog/event-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { EventNewComponent } from "../event-new/event-new.component";
import { ApiService } from "src/app/services/api.service";
import moment from "moment";
import { SessionService } from "src/app/services/session.service";
import { AlertDialogService } from "src/app/services/alert-dialog.service";
import { TaskManagementDialogComponent } from "src/app/components/direct/components/task-management-dialog/task-management-dialog.component";
import {
  TaskMgmtService,
  TaskModel,
} from "src/app/components/direct/service/task-mgmt.service";

@Component({
  selector: "app-event-schedule",
  templateUrl: "./event-schedule.component.html",
  styleUrls: ["./event-schedule.component.scss"],
})
export class EventScheduleComponent implements OnInit, AfterViewInit {
  calendarOptions: CalendarOptions;
  events: any[] = [];
  username: string = "";
  selectedDay: any = null;
  lastClickTime = 0;

  showEvents = true;
  showTasks = true;

  constructor(
    private dialog: MatDialog,
    private apiService: ApiService,
    private sessionService: SessionService,
    private dialogService: DialogService,
    private alertDialogService: AlertDialogService,
    private taskService: TaskMgmtService
  ) {
    this.username = this.sessionService.getUsernameLocal();

    this.calendarOptions = {
      initialView: "timeGridWeek",
      plugins: [dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin],
      headerToolbar: {
        left: "today prev,next",
        // shareCalendarButton
        center: "title",
        // ✅ Create first, then views
        right: "customCreateMenuButton timeGridDay,timeGridWeek,dayGridMonth",
      },
      buttonText: {
        today: "Today",
        day: "Day",
        week: "Week",
        month: "Month",
      },
      customButtons: {
        customCreateMenuButton: {
          text: "Create +",
          click: () => {},
        },
        // shareCalendarButton: {
        //   text: "",
        //   click: this.shareCalender.bind(this),
        // },
      },
      editable: true,
      selectable: true,
      events: [],
      dateClick: this.onDateClick.bind(this),
      eventClick: this.onEventClick.bind(this),
      eventChange: this.onEventChange.bind(this),
      eventContent: this.eventContent.bind(this),
      eventDidMount: (info: EventMountArg) => this.addEventTooltip(info),
      eventClassNames: this.eventClassNames.bind(this), // keep this for styling
    };
  }

  ngOnInit(): void {
    const postData: any = {
      brand_key: 200101,
      user_email: this.username,
    };

    // 1) Load Outlook events first
    this.apiService.getAllEvent(postData).subscribe((res: any) => {
      const outlookEvents = this.convertEventsToCalendarFormat(res);

      // 2) Load mock tasks (only first time)
      this.taskService.loadInitialTasks().subscribe((tasks) => {
        const taskEvents = this.convertTasksToCalendarFormat(tasks);

        // 3) Merge → show on calendar
        this.events = [...outlookEvents, ...taskEvents];
        // this.calendarOptions = {
        //   ...this.calendarOptions,
        //   events: [...this.events],
        // };
        this.applyEventFilter();
      });
    });

    // 4) Existing triggerAction logic unchanged
    this.apiService.triggerAction$.subscribe((result: any) => {
      if (result?.status === "delete") {
        const index = this.events.findIndex(
          (e: any) => Number(e.id) === result.id
        );
        if (index !== -1) {
          this.events.splice(index, 1);
        }
        // this.calendarOptions = {
        //   ...this.calendarOptions,
        //   events: [...this.events],
        // };
        this.applyEventFilter();
      }

      if (result?.status === "edit") {
        const EditEvent = this.events.find(
          (e: any) => Number(e.id) === result.id
        );
        result.eventData["date"] = moment(result.eventData.start_date).format(
          "YYYY-MM-DD"
        );
        result.eventData["reminder"] =
          result.eventData.reminder == null
            ? "5 Minutes Before"
            : result.eventData.reminder;

        this.dialog
          .open(EventNewComponent, {
            data: {
              eventData: result.eventData,
              buttonText: "Update",
            },
            width: "450px",
            height: "auto",
            disableClose: true,
          })
          .afterClosed()
          .subscribe({
            next: (result) => {
              if (result) {
                setTimeout(() => {
                  const postData = {
                    brand_key: 200101,
                    user_email: this.username,
                  };

                  this.apiService.getAllEvent(postData).subscribe({
                    next: (res: any) => {
                      this.events = this.convertEventsToCalendarFormat(res);
                      this.calendarOptions.events = [...this.events];
                    },
                    error: (err: any) => {
                      console.error("Failed to fetch updated events:", err);
                      alert("Could not refresh events.");
                    },
                  });
                }, 1000);
              }
            },
            error: (err) => console.error("Dialog error:", err),
          });
      }
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      // 1) convert Create button to dropdown
      this.setupCreateDropdown();

      // 2) inject Events / Tasks filters
      this.injectFilterCheckboxes();

      // 3) share icon
      this.injectShareIcon();

      // 4) optional IDs/classes for view buttons
      this.addCustomIdForViewButtons();

      // 5) 👉 finally, force the right-side order
      this.reorderRightToolbar();
    }, 200);
  }

  private reorderRightToolbar(): void {
    // Get the header toolbar
    const header = document.querySelector(
      ".fc-header-toolbar"
    ) as HTMLElement | null;
    if (!header) return;

    const chunks = header.querySelectorAll(".fc-toolbar-chunk");
    if (!chunks.length) return;

    // Right-most chunk (where views + create live)
    const rightChunk = chunks[chunks.length - 1] as HTMLElement;

    // Our injected elements
    const filters = document.getElementById(
      "calendar-filter-box"
    ) as HTMLElement | null;
    const createWrapper = rightChunk.querySelector(
      ".scraii-create-dropdown-wrapper"
    ) as HTMLElement | null;

    // The view buttons group: the group that contains the day/week/month buttons
    const viewGroupBtn = rightChunk.querySelector(
      ".fc-timeGridDay-button, .fc-timeGridWeek-button, .fc-dayGridMonth-button"
    ) as HTMLElement | null;

    const viewGroup = viewGroupBtn
      ? (viewGroupBtn.closest(".fc-button-group") as HTMLElement | null)
      : null;

    if (!createWrapper || !viewGroup) {
      return;
    }

    // 🔁 Re-append in the desired order:
    // [Events][Tasks]   [Create]   [day|week|month]
    if (filters) {
      rightChunk.appendChild(filters);
    }
    rightChunk.appendChild(createWrapper);
    rightChunk.appendChild(viewGroup);
  }

  addCustomIdForViewButtons() {
    const groups = document.querySelectorAll(".fc-button-group");
    if (!groups.length) return;

    // Example: add ID to the FIRST button group (prev/next/today)
    const firstGroup = groups[0];
    firstGroup.setAttribute("id", "fc-nav-buttons");

    // Example: add class to the SECOND group (day/week/month)
    if (groups[1]) {
      groups[1].classList.add("fc-view-switcher");
    }
  }

  injectShareIcon() {
    const btn = document.querySelector(
      ".fc-shareCalendarButton-button"
    ) as HTMLElement;

    if (!btn) return;

    // Clean existing text
    btn.innerHTML = `
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <path d="M2.66406 8V13.3333C2.66406 13.687 2.80454 14.0261 3.05459 14.2761C3.30464 14.5262 3.64377 14.6667 3.9974 14.6667H11.9974C12.351 14.6667 12.6902 14.5262 12.9402 14.2761C13.1903 14.0261 13.3307 13.687 13.3307 13.3333V8"
        stroke="#1363DF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M10.6693 4.00065L8.0026 1.33398L5.33594 4.00065"
        stroke="#1363DF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M8 1.33398V10.0007" stroke="#1363DF" stroke-width="2"
        stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `;
  }

  private setupCreateDropdown(): void {
    const btn = document.querySelector(
      ".fc-customCreateMenuButton-button"
    ) as HTMLElement | null;

    if (!btn) {
      return;
    }

    // Make the toolbar chunk a positioning context
    const chunk = btn.parentElement as HTMLElement;
    if (!chunk) return;
    chunk.style.position = "relative";

    // Avoid double-injection
    if (chunk.querySelector(".scraii-create-dropdown-wrapper")) {
      return;
    }

    const wrapper = document.createElement("div");
    wrapper.className = "scraii-create-dropdown-wrapper";

    wrapper.innerHTML = `
    <button type="button" class="scraii-create-main-btn">
      Create +
    </button>
    <div class="scraii-create-dropdown-list">
    <button type="button" class="scraii-create-item" data-action="event">
    Create Event
    </button>
    </div>
    `;
    // <button type="button" class="scraii-create-item" data-action="task">
    //   Create Task
    // </button>

    // Hide the original FC button, we use our own
    btn.style.display = "none";
    chunk.appendChild(wrapper);

    const mainBtn = wrapper.querySelector(
      ".scraii-create-main-btn"
    ) as HTMLButtonElement;
    const dropdown = wrapper.querySelector(
      ".scraii-create-dropdown-list"
    ) as HTMLDivElement;

    if (!mainBtn || !dropdown) return;

    const toggleDropdown = (open?: boolean) => {
      if (open === true) {
        dropdown.classList.add("open");
      } else if (open === false) {
        dropdown.classList.remove("open");
      } else {
        dropdown.classList.toggle("open");
      }
    };

    mainBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      toggleDropdown();
    });

    dropdown.addEventListener("click", (e: Event) => {
      const target = e.target as HTMLElement;
      const action = target.getAttribute("data-action");

      if (action === "task") {
        this.onCreateTaskClick();
      } else if (action === "event") {
        this.onAddEventClick(null);
      }

      toggleDropdown(false);
    });

    // Close on outside click
    document.addEventListener("click", (e: Event) => {
      const node = e.target as Node;
      if (!wrapper.contains(node)) {
        toggleDropdown(false);
      }
    });
  }

  // Add CSS classes to differentiate Tasks vs Outlook Events
  eventClassNames(arg: any): string[] {
    const ext: any = arg.event.extendedProps || {};
    const classes: string[] = [];

    if (ext.isTask) {
      classes.push("scraii-calendar-task");
    } else {
      classes.push("scraii-calendar-event");
    }

    return classes;
  }

  // --------------------------------------------------------
  //  Add Event button
  // --------------------------------------------------------
  onAddEventClick(event: any) {
    this.addNewEvent();
  }

  // 👇 NEW: central place to filter what goes into FullCalendar
  private applyEventFilter(): void {
    if (!this.events) {
      this.calendarOptions = { ...this.calendarOptions, events: [] };
      return;
    }

    const filtered = this.events.filter((e: any) => {
      const isTask = !!e.isTask;

      if (isTask && !this.showTasks) return false;
      if (!isTask && !this.showEvents) return false;

      return true;
    });

    this.calendarOptions = {
      ...this.calendarOptions,
      events: [...filtered],
    };
  }

  injectFilterCheckboxes() {
    // after setupCreateDropdown, our wrapper exists
    const anchor =
      (document.querySelector(
        ".scraii-create-dropdown-wrapper"
      ) as HTMLElement | null) ||
      (document.querySelector(
        ".fc-customCreateMenuButton-button"
      ) as HTMLElement | null);

    if (!anchor) return;

    // Avoid duplicate insertion
    if (document.getElementById("calendar-filter-box")) return;

    const container = document.createElement("span");
    container.id = "calendar-filter-box";
    container.className = "calendar-filter-box";

    container.innerHTML = `
    <label class="calendar-filter-pill">
      <input id="filter-events" type="checkbox"
             class="calendar-filter-checkbox" checked />
      <span class="calendar-filter-label-text">Events</span>
    </label>

    `;
    // <label class="calendar-filter-pill">
    //   <input id="filter-tasks" type="checkbox"
    //          class="calendar-filter-checkbox" checked />
    //   <span class="calendar-filter-label-text">Tasks</span>
    // </label>

    // ✅ Insert filters right before the Create dropdown
    anchor.parentElement?.insertBefore(container, anchor);

    const eventChk = document.getElementById(
      "filter-events"
    ) as HTMLInputElement | null;
    const taskChk = document.getElementById(
      "filter-tasks"
    ) as HTMLInputElement | null;

    eventChk?.addEventListener("change", () => {
      this.showEvents = !!eventChk.checked;
      this.applyEventFilter();
    });

    taskChk?.addEventListener("change", () => {
      this.showTasks = !!taskChk.checked;
      this.applyEventFilter();
    });
  }

  onToggleEvents(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.showEvents = input.checked;
    this.applyEventFilter();
  }

  onToggleTasks(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.showTasks = input.checked;
    this.applyEventFilter();
  }

  addEventTooltip(info: EventMountArg): void {
    const event = info.event;
    const isTask = !!event.extendedProps["isTask"];

    const start = event.start;
    const end = event.end;

    let timeText = "";

    if (start && end) {
      timeText = `${moment(start).format("h:mm A")} - ${moment(end).format(
        "h:mm A"
      )}`;
    } else if (start) {
      timeText = moment(start).format("h:mm A");
    }

    const prefix = isTask ? "Task" : "Event";

    // This is the browser-native tooltip (works in month + week + day views)
    info.el.title = `${prefix}: ${event.title}${
      timeText ? "\n" + timeText : ""
    }`;
  }

  // Renders icon + time + title for both tasks & Outlook events
  eventContent(arg: any) {
    const ext: any = arg.event.extendedProps || {};
    const isTask = !!ext["isTask"];

    // Material icon name per type
    const iconName = isTask ? "flag" : "schedule";
    const iconClass = isTask ? "scraii-task-icon" : "scraii-event-icon";

    const timeText = arg.timeText ? arg.timeText + " " : "";
    const title = arg.event.title || "";

    return {
      html: `
      <div class="scraii-fc-event-inner">
        <span class="${iconClass} material-icons">${iconName}</span>
        <span class="scraii-fc-event-text f">
          ${timeText}${title}
        </span>
      </div>
    `,
    };
  }

  addNewEvent() {
    this.dialog
      .open(EventNewComponent, {
        data: {
          eventData: {},
          buttonText: "Create",
        },
        width: "450px",
        height: "auto",
        disableClose: true,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          setTimeout(() => {
            const postData = {
              brand_key: 200101,
              user_email: this.username,
            };

            this.apiService.getAllEvent(postData).subscribe({
              next: (res: any) => {
                this.events = this.convertEventsToCalendarFormat(res);
                this.calendarOptions.events = [...this.events];
              },
              error: (err: any) => {
                console.error("Failed to fetch updated events:", err);
                alert("Could not refresh events.");
              },
            });
          }, 2000);
        }
      });
  }

  // --------------------------------------------------------
  //  Create Task button
  // --------------------------------------------------------
  onCreateTaskClick(): void {
    const baseDate = this.selectedDay ? new Date(this.selectedDay) : new Date();

    const initialTaskData = {
      taskName: "",
      taskType: null,
      assignTo: null,
      startDate: baseDate,
      endDate: baseDate,
      remindMe: false,
      remindInMinutes: null,
      status: "planned",
      priorityLevel: "medium",
      tagType: null,
      tags: [],
      description: "",
      taskStatus: false,
      isCancelled: false,
    };

    const dialogRef = this.dialog.open(TaskManagementDialogComponent, {
      width: "80vw",
      maxWidth: "700px",
      autoFocus: false,
      data: initialTaskData,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;

      this.taskService.addTask(result).subscribe((newTask) => {
        const taskEvent = this.convertTasksToCalendarFormat([newTask])[0];

        this.events = [...this.events, taskEvent];
        // this.calendarOptions = {
        //   ...this.calendarOptions,
        //   events: [...this.events],
        // };
        this.applyEventFilter();
      });
    });
  }

  // --------------------------------------------------------
  //  Date Click → create Outlook event
  // --------------------------------------------------------
  onDateClick(arg: DateClickArg): void {
    this.selectedDay = arg.date;

    const momentDate = moment(arg.date);
    const date = momentDate.format("YYYY-MM-DD");
    const startTime = momentDate.format("HH:mm");
    const endTime = momentDate.clone().add(30, "minutes").format("HH:mm");

    const newEventData = {
      event_name: "",
      type: "Meeting",
      date,
      start_time: startTime,
      end_time: endTime,
      reminder: "5 Minutes Before",
      description: "This is a scheduled event. Please find the details.",
      meeting_link: "",
      event_created_by: this.username,
      event_created_dt: new Date(),
      event_attendees: [],
    };

    this.dialog
      .open(EventNewComponent, {
        data: {
          eventData: newEventData,
          buttonText: "Create",
        },
        width: "450px",
        height: "auto",
        disableClose: true,
      })
      .afterClosed()
      .subscribe({
        next: (result) => {
          if (result) {
            setTimeout(() => {
              const postData = {
                brand_key: 200101,
                user_email: this.username,
              };

              this.apiService.getAllEvent(postData).subscribe({
                next: (res: any) => {
                  this.events = this.convertEventsToCalendarFormat(res);
                  this.calendarOptions.events = [...this.events];
                },
                error: (err: any) => {
                  console.error("Failed to fetch updated events:", err);
                  alert("Could not refresh events.");
                },
              });
            }, 1000);
          }
        },
        error: (err) => {
          console.error("Dialog closed with error:", err);
        },
      });
  }

  // --------------------------------------------------------
  //  Event Click → either Task dialog or Outlook dialog
  // --------------------------------------------------------
  onEventClick(arg: EventClickArg) {
    const now = Date.now();
    if (now - this.lastClickTime < 800) {
      return;
    }
    this.lastClickTime = now;

    const extended: any = arg.event.extendedProps || {};

    // ---------- TASK FLOW ----------
    if (extended["isTask"]) {
      const taskData = extended["taskData"];

      const dialogRef = this.dialog.open(TaskManagementDialogComponent, {
        width: "80vw",
        maxWidth: "700px",
        data: taskData,
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (!result) return;

        this.taskService
          .updateTask({ ...result, task_id: taskData.task_id })
          .subscribe((updated) => {
            const updatedCalEvent = this.convertTasksToCalendarFormat([
              updated,
            ])[0];

            arg.event.setProp("title", updatedCalEvent.title);
            arg.event.setStart(updatedCalEvent.start);
            arg.event.setEnd(updatedCalEvent.end);
            arg.event.setExtendedProp("taskData", updatedCalEvent.taskData);

            const idx = this.events.findIndex(
              (e: any) => e.id === arg.event.id
            );
            if (idx !== -1) this.events[idx] = updatedCalEvent;

            // this.calendarOptions = {
            //   ...this.calendarOptions,
            //   events: [...this.events],
            // };
            this.applyEventFilter();
          });
      });

      return;
    }

    // ---------- OUTLOOK EVENT FLOW ----------
    let eventValue: any = { ...arg.event._def.extendedProps };

    this.apiService
      .getAllAttendees({ event_log_key: arg.event.id, brand_key: 200101 })
      .subscribe((res: any) => {
        (eventValue as any)["event_attendees"] = res;
        this.openDialog(this.transformEvent(eventValue));
      });
  }

  // --------------------------------------------------------
  //  Event drag/resize for Outlook events
  // --------------------------------------------------------
  onEventChange(changeInfo: any): void {
    const extended: any = changeInfo.event.extendedProps || {};

    if (this.username != extended["event_created_by"]) {
      changeInfo.revert();
      return;
    }

    this.alertDialogService
      .confirmationWindow("Are you sure you want to update this event?")
      .then((result: any) => {
        if (!result) {
          changeInfo.revert();
          return;
        }

        const startMoment = moment.utc(changeInfo.event.start).local();
        const endMoment = moment.utc(changeInfo.event.end).local();

        const startDate = startMoment.format("YYYY-MM-DD");
        const endDate = endMoment.format("YYYY-MM-DD");
        const startTime = startMoment.format("HH:mm");
        const endTime = endMoment.format("HH:mm");

        let updatedEvent: any = {
          start_date: startDate,
          end_date: endDate,
          start_time: startTime,
          end_time: endTime,
        };

        this.apiService
          .getAllAttendees({
            event_log_key: extended["event_log_key"],
            brand_key: 200101,
          })
          .subscribe({
            next: (res: any) => {
              const postData = {
                ...extended,
                ...updatedEvent,
                event_attendees: res,
              };

              this.apiService.addEvent(postData).subscribe(
                () => {
                  this.dialogService.openSnackBar(
                    "Event updated successfully.",
                    "OK"
                  );
                },
                (error) => {
                  console.error("Error while updating event:", error);
                  this.dialogService.openSnackBar(
                    "Error while updating event..",
                    "OK"
                  );
                }
              );
            },
            error: (err: any) => {
              console.error("Error fetching participants:", err);
              this.dialogService.openSnackBar(
                "Could not fetch participants..",
                "OK"
              );
            },
          });
      })
      .catch(() => {
        changeInfo.revert();
      });
  }

  // --------------------------------------------------------
  //  Open Outlook Event Detail Dialog
  // --------------------------------------------------------
  openDialog(data: any) {
    this.dialog
      .open(EventDialogComponent, {
        data,
        width: "500px",
        height: "auto",
        disableClose: true,
      })
      .afterClosed()
      .subscribe((result: any) => {
        if (result?.status == "delete") {
          this.events = this.events.filter((e: any) => e.id !== result.id);
          this.calendarOptions.events = [...this.events];
        }
      });
  }

  // --------------------------------------------------------
  //  Convert API Outlook event → dialog view model
  // --------------------------------------------------------
  transformEvent(rawEvent: any): any {
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const formatDate = (dateStr: string) => {
      const date = new Date(dateStr);
      return `${date.getDate()} ${
        monthNames[date.getMonth()]
      } ${date.getFullYear()} (${dayNames[date.getDay()]})`;
    };

    const formatTime = (time: string) => {
      const [hour, minute] = time.split(":").map(Number);
      const suffix = hour >= 12 ? "PM" : "AM";
      const hour12 = hour % 12 || 12;
      return `${hour12.toString().padStart(2, "0")}:${minute
        .toString()
        .padStart(2, "0")} ${suffix}`;
    };

    const calculateDuration = (startTime: string, endTime: string) => {
      const [sh, sm] = startTime.split(":").map(Number);
      const [eh, em] = endTime.split(":").map(Number);
      const durationMinutes = eh * 60 + em - (sh * 60 + sm);
      return `${durationMinutes} Min`;
    };

    const dedupedEmails = Array.from(
      new Set(
        (rawEvent.event_attendees || [])
          .map((p: any) => p)
          .filter(
            (email: any) =>
              email.event_attendees_email !== rawEvent.event_created_by
          )
      )
    );

    const event_attendees = [
      {
        name: rawEvent.event_created_by
          .split("@")[0]
          .replace(".", " ")
          .replace(/\b\w/g, (c: any) => c.toUpperCase()),
        role: "Organizer",
        status: "",
        isYou: true,
        roleColor: "#007aff",
        email: rawEvent.event_created_by,
      },
      ...dedupedEmails.map((email: any) => ({
        name: email.event_attendees_email
          .split("@")[0]
          .replace(".", " ")
          .replace(/\b\w/g, (c: any) => c.toUpperCase()),
        role: "Attendee",
        status: email.event_attendee_status || "Invited",
        isYou: false,
        roleColor: "#5e5ce6",
        email: email.event_attendees_email,
      })),
    ];

    return {
      title: rawEvent.event_name,
      subtitle: rawEvent.type,
      date: formatDate(rawEvent.start_date),
      time: `${formatTime(rawEvent.start_time)} - ${formatTime(
        rawEvent.end_time
      )}`,
      duration: calculateDuration(rawEvent.start_time, rawEvent.end_time),
      event_attendees,
      meetingLink:
        rawEvent?.meeting_link || "https://meet.google.com/rtey-dsas-mtpqr",
      description: rawEvent.description,
      createdBy: rawEvent.event_created_by,
      event_log_key: rawEvent.event_log_key,
      diaplayActions: true,
      rawData: rawEvent,
      invitedBy: rawEvent.event_created_by
        .split("@")[0]
        .replace(".", " ")
        .replace(/\b\w/g, (c: any) => c.toUpperCase()),
    };
  }

  // --------------------------------------------------------
  //  Outlook events → FullCalendar
  // --------------------------------------------------------
  convertEventsToCalendarFormat(originalEvents: any[]): any[] {
    return originalEvents.map((event) => {
      const start = new Date(
        `${event.start_date.split("T")[0]}T${event.start_time}:00`
      );
      const end = new Date(
        `${event.end_date.split("T")[0]}T${event.end_time}:00`
      );

      return {
        id: String(event.event_log_key),
        title: event.event_name,
        start,
        end,
        allDay: false,
        participants: [],
        ...event,
      };
    });
  }

  // --------------------------------------------------------
  //  Tasks → FullCalendar
  // --------------------------------------------------------
  // convertTasksToCalendarFormat(tasks: any[]): any[] {
  //   return tasks.map((task: TaskModel) => {
  //     const start = task.startDate ? new Date(task.startDate) : new Date();
  //     const end = task.endDate ? new Date(task.endDate) : start;

  //     return {
  //       id: `task-${task.task_id}`,
  //       title: task.taskName,
  //       start,
  //       end,
  //       allDay: false,
  //       isTask: true,
  //       taskData: task, // full data for editing
  //     };
  //   });
  // }

  // Convert tasks from backend to FullCalendar events
  convertTasksToCalendarFormat(tasks: TaskModel[]): any[] {
    const priorityColors: Record<
      string,
      { bg: string; border: string; text: string }
    > = {
      low: { bg: "#dcfce7", border: "#22c55e", text: "#166534" }, // green-ish
      medium: { bg: "#ffedd5", border: "#f97316", text: "#9a3412" }, // orange-ish
      high: { bg: "#fee2e2", border: "#ef4444", text: "#991b1b" }, // red-ish
    };

    return tasks.map((task: TaskModel) => {
      const start = task.startDate ? new Date(task.startDate) : new Date();
      const end = task.endDate ? new Date(task.endDate) : start;

      const colors = priorityColors[task.priorityLevel] || {
        bg: "#e5e7eb",
        border: "#6b7280",
        text: "#111827",
      };

      return {
        id: `task-${task.task_id}`, // unique ID
        title: task.taskName,
        start,
        end,
        allDay: false,
        isTask: true,
        taskData: task,

        // 🎨 visual styles (FullCalendar)
        backgroundColor: colors.bg,
        borderColor: colors.border,
        textColor: colors.text,
      };
    });
  }

  shareCalender(): void {
    alert("Share calendar clicked!");
    // later you can replace this with your real share modal/dialog
  }
}
